import * as React from "react"
import { Clock } from "lucide-react"

import { cn } from "../../lib/utils"
import { Button } from "./button"
import { Popover, PopoverContent, PopoverTrigger } from "./popover"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./select"

interface TimePickerProps {
  time: string | undefined
  onTimeChange: (time: string | undefined) => void
  placeholder?: string
  className?: string
  disabled?: boolean
}

export function TimePicker({
  time,
  onTimeChange,
  placeholder = "Pick a time",
  className,
  disabled = false,
}: TimePickerProps) {
  const hours = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, "0"))
  const minutes = Array.from({ length: 12 }, (_, i) => (i * 5).toString().padStart(2, "0"))

  const [selectedHour, setSelectedHour] = React.useState<string>(() => {
    if (time) {
      const [h] = time.split(":")
      return h
    }
    return ""
  })

  const [selectedMinute, setSelectedMinute] = React.useState<string>(() => {
    if (time) {
      const [, m] = time.split(":")
      return m
    }
    return ""
  })

  React.useEffect(() => {
    if (time) {
      const [h, m] = time.split(":")
      setSelectedHour(h)
      setSelectedMinute(m)
    } else {
      setSelectedHour("")
      setSelectedMinute("")
    }
  }, [time])

  const handleHourChange = (hour: string) => {
    setSelectedHour(hour)
    if (hour && selectedMinute) {
      onTimeChange(`${hour}:${selectedMinute}`)
    } else if (hour) {
      onTimeChange(`${hour}:00`)
    }
  }

  const handleMinuteChange = (minute: string) => {
    setSelectedMinute(minute)
    if (selectedHour && minute) {
      onTimeChange(`${selectedHour}:${minute}`)
    }
  }

  const displayTime = time ? time.substring(0, 5) : null

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          className={cn(
            "w-full justify-start text-left font-normal",
            !time && "text-muted-foreground",
            className
          )}
        >
          <Clock className="ltr:mr-2 rtl:ml-2 h-4 w-4" />
          {displayTime ? <span>{displayTime}</span> : <span>{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-4" align="start">
        <div className="flex items-center gap-2">
          <div className="flex flex-col gap-1">
            <span className="text-xs text-muted-foreground">Hour</span>
            <Select value={selectedHour} onValueChange={handleHourChange}>
              <SelectTrigger className="w-[70px]">
                <SelectValue placeholder="HH" />
              </SelectTrigger>
              <SelectContent className="max-h-[200px]">
                {hours.map((hour) => (
                  <SelectItem key={hour} value={hour}>
                    {hour}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <span className="text-xl mt-5">:</span>
          <div className="flex flex-col gap-1">
            <span className="text-xs text-muted-foreground">Minute</span>
            <Select value={selectedMinute} onValueChange={handleMinuteChange}>
              <SelectTrigger className="w-[70px]">
                <SelectValue placeholder="MM" />
              </SelectTrigger>
              <SelectContent className="max-h-[200px]">
                {minutes.map((minute) => (
                  <SelectItem key={minute} value={minute}>
                    {minute}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        {time && (
          <Button
            variant="ghost"
            size="sm"
            className="mt-2 w-full"
            onClick={() => {
              setSelectedHour("")
              setSelectedMinute("")
              onTimeChange(undefined)
            }}
          >
            Clear
          </Button>
        )}
      </PopoverContent>
    </Popover>
  )
}
